import { inject } from '@angular/core';
import { getIndexDB } from '@services/indexDB';
import { LogService } from '@services/log.service';
import { isNil } from 'lodash';
import { Observable, Subject, defer, firstValueFrom, from } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export abstract class Store<T> {
  protected logService = inject(LogService);

  protected readonly state$ = new Subject<[string, T | null]>();
  protected readonly pending = new Map<string, Promise<T>>();

  protected abstract getDataSource(key: string): Observable<T>;

  getNamespace(): string {
    return this.constructor.name;
  }

  watch(key: string): Observable<T | null> {
    return this.state$.pipe(
      filter(([itemKey]) => key === itemKey),
      map(([, value]) => value),
    );
  }

  get(key: string): Observable<T> {
    return defer(() => from(this.getOrLoad(key)));
  }

  async keys(): Promise<string[]> {
    const db = await getIndexDB();
    const keys = await db.getAllKeys(this.getNamespace());
    return keys.map(key => key.toString());
  }

  async getOrLoad(key: string): Promise<T> {
    const db = await getIndexDB();
    const item = await db.get(this.getNamespace(), key);
    if (!isNil(item)) return item;
    return await this.load(key);
  }

  async getOne(key: string): Promise<T> {
    const db = await getIndexDB();
    return await db.get(this.getNamespace(), key);
  }

  async getMany(keys: string[] = []): Promise<Record<string, T>> {
    if (keys.length === 0) keys = await this.keys();
    const entries = await Promise.all(keys.map(async key => [key, await this.getOne(key)] as const));
    return Object.fromEntries(entries);
  }

  async load(key: string): Promise<T> {
    const pending = this.pending.get(key);
    if (pending) return pending;

    const loadPromise = async () => {
      try {
        const result = await firstValueFrom(this.getDataSource(key), { defaultValue: null as T });
        if (!isNil(result)) {
          await this.set(key, result);
        }
        return result;
      } finally {
        this.pending.delete(key);
      }
    };

    const promise = loadPromise();
    this.pending.set(key, promise);
    return promise;
  }

  async set(key: string, value: T) {
    const db = await getIndexDB();
    await db.put(this.getNamespace(), value, key);
    this.state$.next([key, value]);
  }

  async update(key: string, updateFn: (value: T) => T) {
    const currentValue = await this.getOrLoad(key);
    const updatedValue = updateFn(currentValue);
    await this.set(key, updatedValue);
  }

  async remove(key: string) {
    const db = await getIndexDB();
    await db.delete(this.getNamespace(), key);
    this.state$.next([key, null]);
  }

  async reset() {
    const db = await getIndexDB();
    const keys = await this.keys();
    await db.clear(this.getNamespace());
    keys.forEach(key => this.state$.next([key, null]));
  }

  async refresh() {
    const keys = await this.keys();
    await Promise.all(keys.map(key => this.load(key)));
  }
}
